<template>
  <div
    id="seek-bar-container"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div
      id="seek-bar-base"
      ref="seekBarBase"
    >
      <div
        id="seek-bar-played"
        ref="seekBarPlayed"
      >
        <div
          id="seek-bar-handle"
          ref="seekBarHandle"
          v-show="$data.$_isCursorOnClickableArea"
        >
        </div>
      </div>

      <div
        id="seek-bar-loop"
        class="loop"
        ref="seekBarLoop"
        v-show="isLoopEnabled"
      >
      </div>

      <div
        v-for="(markerSegmentStyleAndClass, markerSegmentIdx) of $_markerSegmentStyleAndClasses"
        v-bind:key="markerSegmentIdx"
        v-bind:style="markerSegmentStyleAndClass.style.markerSegment"
        v-bind:class="markerSegmentStyleAndClass.class"
      >
        <div
          class="seek-bar-marker-label overlay-text-box"
          v-if="markerSegmentStyleAndClass.isCurrentMarkerSegment"
          v-bind:style="markerSegmentStyleAndClass.style.markerLabel"
        >
          {{ sortedMarkerSegments[markerSegmentIdx].data.name }}
        </div>
      </div>

      <div
        id="seek-bar-clickable-area"
        ref="seekBarClickableArea"
        v-on:mouseover="$_onCursorOnClickableArea(true)"
        v-on:mouseout="$_onCursorOnClickableArea(false)"
        v-on:mousedown.stop="$_seekStart"
      >
      </div>
    </div>
  </div>
</template>

<style scoped>
div#seek-bar-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 10px;
}

div#seek-bar-base {
  position: relative;
  flex-grow: 1;
  height: 3px;
  background-color: #444444;
}

div#seek-bar-played {
  height: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

div#seek-bar-handle {
  flex-shrink: 0;
  background-color: #ffffff;
  height: 11px;
  width: 11px;
  margin-top: -1px;
  margin-right: -6px;
  border-radius: 50%;
}

div.seek-bar-marker-segment,
div#seek-bar-loop {
  position: absolute;
  top: 0;
  height: 100%;
  opacity: 0.5;
}

div.seek-bar-marker-label {
  position: absolute;
  padding: 2px 5px;
  font-size: 10px;
  font-weight: bold;
  white-space: nowrap;
}

div#seek-bar-clickable-area {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin-top: -10px;
  padding: 10px 0;
}
</style>

<style src="./video_player.css" scoped></style>

<script>
import LoopDefinition from '../../modules/LoopDefinition.js';
import utils from '../../modules/utils.js';
import TimeValue from '../../modules/TimeValue.js';
import VideoTimeRange from './modules/VideoTimeRange.js';
import MsecDuratives from '../../modules/MsecDuratives.js';

export default {
  watch: {
    currentTime: {
      handler(newCurrentTime) { this.$_updateSeekBarPosition(newCurrentTime) },
      deep: true,
    },

    $_seekBarDuration: {
      handler(newSeekBarDuration) {
        if (newSeekBarDuration === undefined) return;
        let seekBarBaseWidthPx = this.$refs.seekBarBase.getBoundingClientRect().width;
        this.$_updateSeekBarScaleAndPosition(seekBarBaseWidthPx, newSeekBarDuration);
      },
      deep: true,
    },

    loopDefinition: {
      handler(newLoopDefinition) {
        if (newLoopDefinition === undefined) return;
        this.$_updateLoopRange(newLoopDefinition);
      },
      deep: true,
    },
  },

  mounted() {
    this.$_updateSeekBarScaleAndPosition(this.$refs.seekBarBase.getBoundingClientRect().width, this.duration);
    this.$data.$_seekBarBaseResizeObserver.observe(this.$refs.seekBarBase);
    window.addEventListener('mousemove', this.$_seek);
    window.addEventListener('mouseup', this.$_seekEnd);
  },

  destroyed() {
    window.removeEventListener('mouseup', this.$_seek);
    window.removeEventListener('mousemove', this.$_seekEnd);
    this.$data.$_seekBarBaseResizeObserver.disconnect();
  },

  props: {
    currentTime:          { type: TimeValue },
    duration:             { type: TimeValue },
    videoTimeRange:       { type: VideoTimeRange },
    isLoopEnabled:        { type: Boolean },
    loopDefinition:       { type: LoopDefinition, required: true },
    sortedMarkerSegments: { type: Array },
  },

  data() {
    return {
      $_seekBarBaseResizeObserver: new ResizeObserver(this.$_onResize),
      $_seekBarScale: 1,
      $_isSeeking: false,
      $_isCursorOnClickableArea: false,
    };
  },

  computed: {
    $_seekBarBegin() {
      return this.videoTimeRange.begin.ceilTo(this.$_seekBarEnd);
    },

    $_seekBarEnd() {
      return this.videoTimeRange.end.ceilTo(this.duration);
    },

    $_seekBarDuration() {
      return this.$_seekBarEnd.getOffsetFrom(this.$_seekBarBegin);
    },

    $_seekBarLocalLoopDefinition() {
      return new LoopDefinition(
        this.loopDefinition.begin.getOffsetFrom(this.$_seekBarBegin).floorTo(TimeValue.zero),
        this.loopDefinition.end.getOffsetFrom(this.$_seekBarBegin).ceilTo(this.$_seekBarDuration),
      );
    },

    $_sortedPhysicalMarkerRanges() {
      return this.sortedMarkerSegments.map(markerSegment => new MsecDuratives(
        (markerSegment.begin === null)? TimeValue.zero : markerSegment.begin,
        (markerSegment.end === null)? this.duration : markerSegment.end,
      ));
    },

    $_markerSegmentStyleAndClasses() {
      let currentMarkerCount = 0;
      return this.$_sortedPhysicalMarkerRanges.map(markerRange => {
        if (
          (this.$_seekBarDuration === undefined) ||
          markerRange.begin.isGreaterThanOrEqualTo(this.$_seekBarEnd) ||
          markerRange.end.isLessThan(this.$_seekBarBegin)
        ) {
          return { style: { markerSegment: { display: 'none' } } };
        }
        let seekBarLocalSegmentBegin = markerRange.begin.getOffsetFrom(this.$_seekBarBegin).floorTo(TimeValue.zero);
        let seekBarLocalSegmentEnd = markerRange.end.getOffsetFrom(this.$_seekBarBegin).ceilTo(this.$_seekBarDuration);
        let seekBarLocalSegmentDuration = seekBarLocalSegmentEnd.getOffsetFrom(seekBarLocalSegmentBegin);
        let seekBarClassList = [ 'seek-bar-marker-segment', 'marker-segment' ];
        let isCurrentMarkerSegment = markerRange.isInRange(this.currentTime);
        if (isCurrentMarkerSegment) {
          ++currentMarkerCount;
          seekBarClassList.push('current-marker-segment')
        }
        return {
          style:{
            markerSegment: {
              left: `${this.$_timeToScaledPx(seekBarLocalSegmentBegin)}px`,
              width: `${this.$_timeToScaledPx(seekBarLocalSegmentDuration)}px`,
            },
            markerLabel: {
              bottom: `${-10 + 20 * currentMarkerCount}px`,
            },
          },
          class: seekBarClassList,
          isCurrentMarkerSegment: isCurrentMarkerSegment,
        };
      });
    },
  },

  methods: {
    $_onResize(resizeObserverEntries) {
      let resizeObserverEntry = resizeObserverEntries[0];
      this.$_updateSeekBarScaleAndPosition(resizeObserverEntry.contentRect.width, this.$_seekBarDuration);
    },

    $_timeToScaledPx(time) {
      return time.msec / this.$data.$_seekBarScale;
    },

    $_scaledPxToTime(px) {
      return TimeValue.msec(px * this.$data.$_seekBarScale);
    },

    $_updateSeekBarPosition(currentTime) {
      this.$refs.seekBarPlayed.style.width = `${this.$_timeToScaledPx(currentTime.getOffsetFrom(this.$_seekBarBegin))}px`;
    }, 

    $_updateSeekBarScaleAndPosition(seekBarBaseWidthPx, seekBarDuration) {
      this.$data.$_seekBarScale = seekBarDuration.msec / seekBarBaseWidthPx;
      this.$_updateSeekBarPosition(this.currentTime);
      this.$_updateLoopRange(this.$_seekBarLocalLoopDefinition);
    },

    $_updateLoopRange(seekBarLocalLoopDefinition) {
      this.$refs.seekBarLoop.style.left = `${this.$_timeToScaledPx(seekBarLocalLoopDefinition.begin)}px`;
      this.$refs.seekBarLoop.style.width = `${this.$_timeToScaledPx(seekBarLocalLoopDefinition.duration)}px`;
    },

    $_getSeekTimeMsecByMouseEvent(mouseEvent) {
      let seekBarBaseClientRect = this.$refs.seekBarBase.getBoundingClientRect();
      let seekBarOffsetPx = mouseEvent.clientX - seekBarBaseClientRect.x;
      return seekBarOffsetPx * this.$data.$_seekBarScale;
    },

    $_seek(event) {
      if (this.$data.$_isSeeking) {
        let seekBarBaseClientRect = this.$refs.seekBarBase.getBoundingClientRect();
        let seekBarLocalOffsetPx = event.clientX - seekBarBaseClientRect.x;
        let seekTime = this.$_scaledPxToTime(seekBarLocalOffsetPx).clampTo(TimeValue.zero, this.$_seekBarDuration).add(this.$_seekBarBegin);
        this.$emit('seek', seekTime);
      }
    },

    $_seekStart(event) {
      this.$data.$_isSeeking = true;
      this.$emit('seek-start');
      this.$_setIsCursorOnClickableArea(true);
      this.$_seek(event);
    },

    $_seekEnd(event) {
      this.$data.$_isSeeking = false;
      this.$_seek(event);
      this.$_updateIsCursorOnClickableArea(event);
      this.$emit('seek-end');
    },

    $_updateIsCursorOnClickableArea(event) {
      let seekBarClickableAreaBoundingClientRect = this.$refs.seekBarClickableArea.getBoundingClientRect();
      this.$_setIsCursorOnClickableArea(utils.isInRect(seekBarClickableAreaBoundingClientRect, event.clientX, event.clientY));
    },

    $_setIsCursorOnClickableArea(displaysHandle) {
      this.$data.$_isCursorOnClickableArea = displaysHandle;
    },

    $_onCursorOnClickableArea(displaysHandle) {
      if (!this.$data.$_isSeeking) {
        this.$_setIsCursorOnClickableArea(displaysHandle);
      }
    },
  },
}
</script>